import React from 'react';
import { Check, ArrowRight, Building2, Server, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const EnergyStoragePage = () => {
  const specs = [
    { label: 'Chemistry', value: 'Multi-Ion' },
    { label: 'Voltage', value: '400V DC' },
    { label: 'Capacity Range', value: '50kWh - 1MWh' },
    { label: 'Cycle Life', value: '10,000+ cycles' },
    { label: 'Round-trip Efficiency', value: '>90%' },
    { label: 'Operating Temp', value: '-40°C to +65°C' },
    { label: 'Warranty', value: '15 years' },
    { label: 'Monitoring', value: 'Remote 24/7' },
  ];

  const applications = [
    { 
      name: 'Commercial & Industrial', 
      icon: Building2, 
      description: 'Peak shaving, demand charge reduction',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&q=80'
    },
    { 
      name: 'Data Centers & Telecom', 
      icon: Server, 
      description: 'Backup power, UPS replacement',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&q=80'
    },
    { 
      name: 'Grid Scale', 
      icon: Zap, 
      description: 'Frequency regulation, renewable integration',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=600&q=80'
    },
  ];

  const features = [
    'Modular rack design scales from 50kWh to multi-MWh',
    '10,000+ cycles with minimal degradation',
    '>90% round-trip efficiency',
    'Grid-tie inverter compatible',
    'Operates without active cooling up to 65°C',
    'Remote monitoring and control platform',
    'Revenue generation through grid services',
    '15-year warranty',
  ];

  return (
    <div className="pt-20">
      <SEOHead 
        title="Energy Storage Systems India | ESS Battery | Commercial & Industrial"
        description="Industrial energy storage systems (ESS) in India. Multi-Ion batteries for commercial, industrial, and grid-scale applications. 10,000+ cycle life, 15-year warranty. Backup power, peak shaving, and renewable integration."
        keywords="ESS battery India, energy storage systems India, commercial ESS, industrial ESS, grid scale battery, backup power system, UPS battery India, home energy storage, residential battery storage, solar battery India, 400V battery"
        canonicalPath="/products/energy-storage"
      />
      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-gray-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=1920')] bg-cover bg-center opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/products" className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 mb-8">
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to Products
          </Link>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Energy <span className="text-emerald-400">Storage</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mb-8">
            Scalable Multi-Ion energy storage systems for commercial, industrial, and grid-scale applications.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-8 py-4 rounded-xl font-semibold transition-all"
          >
            Request Specifications <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Specs & Features */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-white mb-8">Specifications</h2>
              <div className="grid grid-cols-2 gap-4">
                {specs.map((spec) => (
                  <div key={spec.label} className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                    <p className="text-gray-500 text-sm mb-1">{spec.label}</p>
                    <p className="text-white font-semibold">{spec.value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-white mb-8">Key Features</h2>
              <div className="space-y-4">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-emerald-400" />
                    </div>
                    <p className="text-gray-300">{feature}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Applications with Images */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Applications</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {applications.map((app) => {
              const Icon = app.icon;
              return (
                <div key={app.name} className="group bg-gray-800/50 rounded-2xl border border-gray-700 overflow-hidden hover:border-emerald-500/30 transition-all duration-300">
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={app.image} 
                      alt={app.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center backdrop-blur-sm">
                        <Icon className="w-6 h-6 text-emerald-400" />
                      </div>
                    </div>
                  </div>
                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-white font-semibold text-xl mb-2">{app.name}</h3>
                    <p className="text-gray-500">{app.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to deploy energy storage?</h2>
          <p className="text-gray-400 text-lg mb-8">
            Contact our team for site assessment and custom solution design.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-8 py-4 rounded-xl font-semibold transition-all"
          >
            Talk to Engineering <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default EnergyStoragePage;
